import { SwapRightOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  theme
} from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  CONFIG,
  MOMENT_CANTIDAD_UNIDAD_MEDIDA,
  UNIDAD_MEDIDA_FECHAS
} from '../constants';
import { useSolicitudContext } from '../context/solicitud-context';
import {
  formatNumberOnKeyPress,
  ordenarFrecuencias,
  ordenarVigencias
} from '../utils';
import CustomDatePicker from './CustomDatePicker';
import TextWithEditButton from './TextWithEditButton';

/*
  Formulario de Vigencia y Formulario de Pagos, los datos son validados segun datos configurados en la Vigencia de la Empresa y/o Aseguradora del Producto.
*/
const { useToken } = theme;
const dateFormat = 'DD/MM/YYYY';

const NewForm = ({ children, vigenciaForm, setForm }) => {
  const [formVigencia] = Form.useForm();

  useEffect(() => {
    if (formVigencia) {
      setForm(formVigencia);
    }
  }, [formVigencia, setForm]);

  return (
    <Form
      name="vigencia"
      layout="horizontal"
      initialValues={vigenciaForm}
      form={formVigencia}
      autoComplete="off"
      labelCol={{ span: 12 }}
      wrapperCol={{ span: 12 }}
    >
      {children}
    </Form>
  );
};

const VigenciaForm = ({
  vigenciaSugeridaConfig,
  setValidacionByAseguradoraList,
  withForm,
  style,
  onInit,
  center,
  loading,
  hidden,
  type = 'primary',
  onDebounce
}) => {
  const [form, setForm] = useState();
  const screens = useBreakpoint();
  const [vigenciaForm, setVigenciaForm] = useState({
    vigencia_sugerida: null,
    desde: null,
    hasta: null,
    cantidad_dias: null,
    cantidad_pagos: null,
    id_vigencia: null,
    id_modalidad_pago: null,
    vigencia: null,
    frecuencia: null
  });
  const { token } = useToken();
  const [frecuenciasHabilitadas, setFrecuenciasHabilitadas] = useState([]);
  const [vigenciasHabilitadas, setVigenciasHabilitadas] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [datosSugeridosCargados, setDatosSugeridosCargados] = useState(false);
  const [desdePermitada, setDesdePermitada] = useState();
  const [datosSugeridos, setDatosSugeridos] = useState();
  const {
    vigenciaPagosContext,
    setVigenciaPagosContext
  } = useSolicitudContext();

  const cantidadPagosText = () => {
    if (vigenciaPagosContext.cantidad_pagos === 1) {
      return `${vigenciaPagosContext.cantidad_pagos} pago`;
    }
    return `${vigenciaPagosContext.cantidad_pagos} pagos`;
  };

  const validarPeriodoCobertura = (values) => {
    const todayMoment = moment();
    const desdeForm = vigenciaForm.desde.clone();
    const unidadMedidaMaximo = UNIDAD_MEDIDA_FECHAS.find(
      (item) => item.codigo === values.cobertura_max_unidad
    );

    const unidadMedidaMinima = UNIDAD_MEDIDA_FECHAS.find(
      (item) => item.codigo === values.cobertura_min_unidad
    );

    const hastaMin = desdeForm
      .clone()
      .add(
        Number(values.cobertura_min_cantidad),
        unidadMedidaMinima.codigo_moment
      );
    const cantidadMinPeriodoCobertura = hastaMin.diff(desdeForm, 'days');

    /* Se valida la retroactividad */
    const unidadMedidaRetroactividad = UNIDAD_MEDIDA_FECHAS.find(
      (item) => item.codigo === values.retroactividad_unidad
    );
    const retroactvidadDesdePermitidaFecha = todayMoment
      .clone()
      .subtract(
        Number(values.retroactividad_cantidad),
        unidadMedidaRetroactividad.codigo_moment
      );
    // SEGU bloquea calendario desde 3 meses atras.
    const retroactvidadDesdePermitidaFechaSegu = todayMoment
      .clone()
      .subtract(Number(3), 'month');
    setDesdePermitada(retroactvidadDesdePermitidaFechaSegu);

    const hastaRetroactividadMaxFecha = retroactvidadDesdePermitidaFecha
      .clone()
      .add(
        Number(values.cobertura_max_cantidad),
        unidadMedidaMaximo.codigo_moment
      );

    const hastaRetroactividadFecha = retroactvidadDesdePermitidaFecha
      .clone()
      .add(Number(vigenciaForm.cantidad_dias), 'days');
    if (desdeForm.isBefore(retroactvidadDesdePermitidaFecha, 'day')) {
      const hastaCalculado = hastaRetroactividadFecha.isAfter(
        hastaRetroactividadMaxFecha
      )
        ? hastaRetroactividadMaxFecha
        : hastaRetroactividadFecha;

      const cantidadDiasRetractividadDiff = hastaCalculado.diff(
        retroactvidadDesdePermitidaFecha,
        'days'
      );
      return {
        desde: retroactvidadDesdePermitidaFecha,
        hasta: hastaCalculado,
        cantidadDiasDiff: cantidadDiasRetractividadDiff,
        validacion: 'Supera la cantidad máxima de retroactividad'
      };
    }

    /* Se calcula y se valida el periodo minimo de la cobertura */

    if (cantidadMinPeriodoCobertura > vigenciaForm.cantidad_dias) {
      return {
        desde: desdeForm,
        hasta: hastaMin,
        cantidadDiasDiff: cantidadMinPeriodoCobertura,
        validacion: 'Supera la cantidad mínima de Vigencia'
      };
    }

    /* Se calcula y se valida el periodo maximo de la cobertura */

    const hastaMax = desdeForm
      .clone()
      .add(
        Number(values.cobertura_max_cantidad),
        unidadMedidaMaximo.codigo_moment
      );

    const cantidadMaxPeriodoCobertura = hastaMax.diff(desdeForm, 'days');
    if (cantidadMaxPeriodoCobertura < vigenciaForm.cantidad_dias) {
      return {
        desde: desdeForm,
        hasta: hastaMax,
        cantidadDiasDiff: cantidadMaxPeriodoCobertura,
        validacion: 'Supera la cantidad máxima de Vigencia'
      };
    }

    /* Se valida la fecha de inicio maxima de vigencia */
    const unidadAgendamientoMax = UNIDAD_MEDIDA_FECHAS.find(
      (item) => item.codigo === values.agendamiento_max_vigencia_unidad
    );
    const fechaDesdeMaxInicioPermitido = todayMoment
      .clone()
      .add(
        Number(values.agendamiento_max_vigencia_cantidad),
        unidadAgendamientoMax.codigo_moment
      );
    const fechaHastaAgendamientoInicio = fechaDesdeMaxInicioPermitido
      .clone()
      .add(Number(vigenciaForm.cantidad_dias), 'days');
    if (desdeForm.isAfter(fechaDesdeMaxInicioPermitido)) {
      return {
        desde: fechaDesdeMaxInicioPermitido,
        hasta: fechaHastaAgendamientoInicio.isAfter(hastaMax)
          ? hastaMax
          : fechaHastaAgendamientoInicio,
        cantidadDiasDiff: 0,
        validacion: 'Supera la cantidad mínima de Vigencia'
      };
    }
    return null;
  };

  const validacionesVigenciaByAseguradora = () => {
    const { configVigenciaAseguradoras } = vigenciaSugeridaConfig;
    let validacionesList = [];

    for (let i = 0; i < configVigenciaAseguradoras.length; i += 1) {
      const config = configVigenciaAseguradoras[i];
      const {
        idAseguradora,
        maximo,
        minimo,
        retroactividad,
        agendamientoMaximo,
        // vigencias,
        frecuencias
      } = config;
      const values = {
        cobertura_min_unidad: minimo.unidadMedida,
        cobertura_min_cantidad: minimo.cantidad,
        cobertura_max_unidad: maximo.unidadMedida,
        cobertura_max_cantidad: maximo.cantidad,
        retroactividad_cantidad: retroactividad.cantidad,
        retroactividad_unidad: retroactividad.unidadMedida,
        agendamiento_max_vigencia_cantidad: agendamientoMaximo.cantidad,
        agendamiento_max_vigencia_unidad: agendamientoMaximo.unidadMedida
      };

      const periodoCoberturaAlert = validarPeriodoCobertura(values);
      const frecuenciaHabilitada = frecuencias.find(
        (item) => item.codigo === vigenciaForm.frecuencia?.codigo
      );
      let vigenciaPagoAlert = false;
      if (periodoCoberturaAlert || !frecuenciaHabilitada) {
        vigenciaPagoAlert = true;
      }

      let mensaje = `Vigencia Solicitada desde ${vigenciaForm.desde.format(
        'DD/MM/YYYY'
      )} hasta ${vigenciaForm.hasta.format('DD/MM/YYYY')} en ${
        vigenciaForm.cantidad_dias
      } día/s (${vigenciaForm.frecuencia?.codigo || ''} - ${
        vigenciaForm.cantidad_pagos > 0
          ? vigenciaForm.cantidad_pagos
          : '0' || '0'
      } pago/s)`;

      if (!frecuenciaHabilitada) {
        mensaje = `La Frecuencia de Pagos  ${
          vigenciaForm.frecuencia?.codigo || ''
        } no está disponible para esta Aseguradora en estos momentos.`;
      }
      validacionesList = [
        ...validacionesList,
        {
          idAseguradora,
          mensaje,
          mostrarAlerta: vigenciaPagoAlert,
          vigencia: periodoCoberturaAlert,
          bloquearPlanes: !frecuenciaHabilitada
        }
      ];

      if (setValidacionByAseguradoraList) {
        setValidacionByAseguradoraList(validacionesList);
      }
    }
  };

  const debouncedUpdateContext = React.useMemo(() => {
    return debounce(setVigenciaPagosContext, 1000);
  }, [setVigenciaPagosContext]);

  const updateContext = React.useCallback(
    (context) => {
      if (onDebounce) {
        onDebounce(true);
      }
      debouncedUpdateContext(context);
    },
    [debouncedUpdateContext, onDebounce]
  );

  const onChangeVigenciaDesde = (date) => {
    const desde = moment(date).utc();
    const hasta = moment(vigenciaForm.hasta).utc();

    const cantidadCalculada = hasta
      .startOf('day')
      .diff(desde.startOf('day'), 'days');
    const cantidadPagosCalculado = calcularCantidadPagos(
      vigenciaForm.frecuencia.codigo,
      date,
      hasta
    );
    const vigenciaUpdate = {
      ...vigenciaForm,
      desde: date,
      cantidad_dias: cantidadCalculada,
      cantidad_pagos: cantidadPagosCalculado,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculado
      }
    };
    setVigenciaForm(vigenciaUpdate);
    habilitarFrecuencias(vigenciaUpdate);
    habilitarVigencias(vigenciaUpdate);

    const vigenciaContextToUpdate = {
      ...vigenciaPagosContext,
      desde: date,
      cantidad_dias: cantidadCalculada,
      cantidad_pagos: cantidadPagosCalculado,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculado
      }
    };
    updateContext(vigenciaContextToUpdate);
  };

  const onChangeVigenciaHasta = (date) => {
    const desde = moment(vigenciaForm.desde).utc();
    const hasta = moment(date).utc();

    const cantidadCalculada = hasta
      .startOf('day')
      .diff(desde.startOf('day'), 'days');

    const cantidadPagosCalculado = calcularCantidadPagos(
      vigenciaForm.frecuencia.codigo,
      vigenciaForm.desde.clone(),
      date
    );
    const vigenciaUpdate = {
      ...vigenciaForm,
      hasta: date,
      cantidad_dias: cantidadCalculada,
      cantidad_pagos: cantidadPagosCalculado,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculado
      }
    };
    setVigenciaForm(vigenciaUpdate);
    habilitarFrecuencias(vigenciaUpdate);
    habilitarVigencias(vigenciaUpdate);

    const vigenciaContextToUpdate = {
      ...vigenciaPagosContext,
      ...vigenciaUpdate
      /* desde: vigenciaUpdate.desde,
      hasta: vigenciaUpdate.hasta */
    };
    updateContext(vigenciaContextToUpdate);
  };

  const onChangeVigenciaDias = (value) => {
    const desde = vigenciaForm.desde.clone();
    const dias = Number(value);
    const hasta = desde.add(dias, 'days');
    const cantidadPagosCalculado = calcularCantidadPagos(
      vigenciaForm.frecuencia.codigo,
      vigenciaForm.desde.clone(),
      hasta
    );
    const vigenciaUpdate = {
      ...vigenciaForm,
      hasta,
      cantidad_dias: dias,
      cantidad_pagos: cantidadPagosCalculado,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculado
      }
    };
    setVigenciaForm(vigenciaUpdate);
    habilitarFrecuencias(vigenciaUpdate);
    habilitarVigencias(vigenciaUpdate);

    const vigenciaContextToUpdate = {
      ...vigenciaPagosContext,
      ...vigenciaUpdate,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculado
      }
    };
    updateContext(vigenciaContextToUpdate);
  };

  const debounceOnChangeVigenciaDias = debounce(async (value) => {
    if (value) {
      onChangeVigenciaDias(value);
    }
  }, 1000);

  const calcularCantidadPagos = (value, fechaDesde, fechaHasta) => {
    const frecuencia = frecuenciasHabilitadas.find(
      (item) => item.codigo === value
    );
    let cantidadPagosCalculo = 1;
    if (frecuencia && frecuencia.codigo !== 'total') {
      const frecuenciaUnidadMoment = MOMENT_CANTIDAD_UNIDAD_MEDIDA.find(
        (item) => item.codigo === frecuencia.codigo
      );
      const diffCantidadUnidadMedidaFrecuencia = fechaHasta.diff(
        fechaDesde,
        frecuenciaUnidadMoment.codigo_moment,
        true
      );
      cantidadPagosCalculo = Math.ceil(
        diffCantidadUnidadMedidaFrecuencia / frecuenciaUnidadMoment.cantidad
      );
    }
    return cantidadPagosCalculo;
  };

  const onChangeFrecuencia = (e) => {
    const { value } = e.target;
    const frecuencia = frecuenciasHabilitadas.find(
      (item) => item.codigo === value
    );
    let cantidadPagosCalculo = 1;
    if (frecuencia.codigo !== 'total') {
      const fechaDesde = vigenciaForm.desde.clone();
      const fechaHasta = vigenciaForm.hasta.clone();
      const frecuenciaUnidadMoment = MOMENT_CANTIDAD_UNIDAD_MEDIDA.find(
        (item) => item.codigo === frecuencia.codigo
      );
      const diffCantidadUnidadMedidaFrecuencia = fechaHasta.diff(
        fechaDesde,
        frecuenciaUnidadMoment.codigo_moment,
        true
      );

      cantidadPagosCalculo = Math.ceil(
        Number(
          diffCantidadUnidadMedidaFrecuencia / frecuenciaUnidadMoment.cantidad
        )
      );
    }
    setVigenciaForm({
      ...vigenciaForm,
      id_modalidad_pago: frecuencia.id,
      frecuencia,
      cantidad_pagos: cantidadPagosCalculo,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculo
      }
    });
    const vigenciaContextToUpdate = {
      ...vigenciaPagosContext,
      ...vigenciaForm,
      desde: vigenciaForm.desde,
      hasta: vigenciaForm.hasta,
      cantidad_pagos: cantidadPagosCalculo,
      frecuencia,
      id_modalidad_pago: frecuencia.id,
      pagos: {
        id_modalidad_pago: frecuencia.id,
        frecuencia,
        cantidad_pagos: cantidadPagosCalculo
      }
    };
    updateContext(vigenciaContextToUpdate);
  };

  const onChangeVigencia = (e) => {
    const { value } = e.target;
    const desdeForm = vigenciaForm.desde.clone();
    const cantidadUnidadMoment = MOMENT_CANTIDAD_UNIDAD_MEDIDA.find(
      (item) => item.codigo === value
    );
    const hastaCalculado = desdeForm
      .clone()
      .add(
        Number(cantidadUnidadMoment.cantidad),
        cantidadUnidadMoment.codigo_moment
      );

    const diffDesdeHasta = hastaCalculado.diff(desdeForm, 'days');

    const vigenciaSelected = vigenciasHabilitadas.find(
      (item) => item.codigo === value
    );

    let cantidadPagosCalculo = Number(1);
    if (vigenciaForm.frecuencia.codigo !== 'total') {
      const frecuenciaUnidadMoment = MOMENT_CANTIDAD_UNIDAD_MEDIDA.find(
        (item) => item.codigo === vigenciaForm.frecuencia.codigo
      );
      const diffCantidadUnidadMedidaFrecuencia = hastaCalculado.diff(
        desdeForm,
        frecuenciaUnidadMoment.codigo_moment,
        true
      );
      cantidadPagosCalculo = Math.ceil(
        Number(
          diffCantidadUnidadMedidaFrecuencia / frecuenciaUnidadMoment.cantidad
        )
      );
    }

    const vigenciaUpdate = {
      ...vigenciaForm,
      hasta: hastaCalculado,
      cantidad_dias: diffDesdeHasta,
      vigencia: vigenciaSelected,
      id_vigencia: vigenciaSelected.id,
      cantidad_pagos: cantidadPagosCalculo,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculo
      }
    };

    setVigenciaForm(vigenciaUpdate);

    habilitarFrecuencias(vigenciaUpdate);

    const vigenciaContextToUpdate = {
      ...vigenciaPagosContext,
      ...vigenciaUpdate,
      pagos: {
        ...vigenciaPagosContext.pagos,
        cantidad_pagos: cantidadPagosCalculo
      }
    };
    updateContext(vigenciaContextToUpdate);
  };

  const habilitarVigencias = (vigenciaUpdate) => {
    // Check or Uncheck de Vigencias de pago que se encuentran en los rangos de las fechas
    let tagVigenciaEncontrado = false;
    const newVigenciasList = vigenciasHabilitadas.map((item) => {
      const cantidadUnidadMoment = MOMENT_CANTIDAD_UNIDAD_MEDIDA.find(
        (unidadMedida) => unidadMedida.codigo === item.codigo
      );

      const { desde } = vigenciaUpdate;
      const hastaCalculado = desde
        .clone()
        .add(
          Number(cantidadUnidadMoment.cantidad),
          cantidadUnidadMoment.codigo_moment
        );
      const diff = hastaCalculado.diff(desde.clone(), 'days');
      // const bloquearVigencia = diff > vigenciaUpdate.cantidad_dias;
      if (diff === Number(vigenciaUpdate.cantidad_dias)) {
        tagVigenciaEncontrado = true;
        const updateVigencia = {
          ...vigenciaUpdate,
          vigencia: item,
          id_vigencia_pago: item.id,
          id_vigencia: item.id,
          dias_vigencia: vigenciaUpdate.cantidad_dias
        };
        setVigenciaForm(updateVigencia);
      }
      return {
        ...item
        // disabled: bloquearVigencia
      };
    });

    if (!tagVigenciaEncontrado) {
      const updateVigencia = {
        ...vigenciaUpdate,
        vigencia: null,
        id_vigencia_pago: null,
        id_vigencia: null,
        dias_vigencia: vigenciaUpdate.cantidad_dias
      };
      setVigenciaForm(updateVigencia);
    }
    const vigenciasHabilitadasOrden = ordenarVigencias(newVigenciasList);

    setVigenciasHabilitadas(vigenciasHabilitadasOrden);
  };

  const habilitarFrecuencias = (vigenciaUpdate, modalidadListParam) => {
    // Deshabilitar frecuencias de pago que no se encuentran en los rangos.
    const modalidadList =
      frecuenciasHabilitadas.length > 0
        ? frecuenciasHabilitadas
        : modalidadListParam;
    if (modalidadList.length > 0) {
      const newFrecuenciaList = modalidadList.map((item) => {
        if (item.codigo !== 'total') {
          const cantidadUnidadMoment = MOMENT_CANTIDAD_UNIDAD_MEDIDA.find(
            (unidadMedida) => unidadMedida.codigo === item.codigo
          );
          const { desde } = vigenciaUpdate;
          const hastaCalculado = moment(desde)
            .clone()
            .add(
              Number(cantidadUnidadMoment.cantidad),
              cantidadUnidadMoment.codigo_moment
            );
          const diff = hastaCalculado.diff(moment(desde).clone(), 'days');
          const bloquearFrecuencia = diff > vigenciaUpdate.cantidad_dias;
          if (
            bloquearFrecuencia &&
            vigenciaUpdate.frecuencia.codigo === item.codigo
          ) {
            const updateVigencia = {
              ...vigenciaUpdate,
              frecuencia: modalidadList.find(
                (elemento) => elemento.codigo === 'total'
              ),
              cantidad_pagos: Number(1)
            };

            setVigenciaForm(updateVigencia);
          }

          return {
            ...item,
            disabled: bloquearFrecuencia
          };
        }
        return item;
      });
      setFrecuenciasHabilitadas(ordenarFrecuencias(newFrecuenciaList));
    }
  };

  const Items = () => (
    <Row
      gutter={[0, 0]}
      style={{
        maxWidth: '1200px',
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'start'
      }}
    >
      <Col xs={24} md={12}>
        <Row
          gutter={[16, 8]}
          style={{
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            textAlign: 'start'
          }}
        >
          <Col xs={24}>
            <Form.Item label="Vigencia" name={['vigencia', 'codigo']}>
              <Radio.Group
                onChange={onChangeVigencia}
                size="small"
                buttonStyle="solid"
              >
                {vigenciasHabilitadas.map(
                  ({ codigo, descripcion, disabled = false }) => (
                    <div
                      key={codigo}
                      style={{ padding: 2, display: 'inline-block' }}
                    >
                      <Radio.Button value={codigo} disabled={disabled}>
                        {descripcion}
                      </Radio.Button>
                    </div>
                  )
                )}
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="Frecuencia de Pagos"
              // name="id_modalidad_pago"
              name={['frecuencia', 'codigo']}
              rules={[{ required: true }]}
            >
              <Radio.Group
                onChange={onChangeFrecuencia}
                size="small"
                buttonStyle="solid"
              >
                {frecuenciasHabilitadas.map(
                  ({ codigo, descripcion, disabled = false }) => (
                    <div
                      key={codigo}
                      style={{ padding: 2, display: 'inline-block' }}
                    >
                      <Radio.Button value={codigo} disabled={disabled}>
                        {descripcion}
                      </Radio.Button>
                    </div>
                  )
                )}
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="Cantidad de Pagos"
              name="cantidad_pagos"
              rules={[{ required: true }]}
            >
              <Input readOnly bordered={false} />
            </Form.Item>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row
          gutter={[16, 8]}
          style={{
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            textAlign: 'start'
          }}
        >
          <Col xs={24}>
            <Form.Item label="Desde" name="desde" rules={[{ required: true }]}>
              <CustomDatePicker
                style={{ width: '100%' }}
                placeholder="DD/MM/YYYY"
                format={dateFormat}
                onChange={onChangeVigenciaDesde}
                disabledDate={(current) => {
                  return (
                    (vigenciaPagosContext && current < desdePermitada) ||
                    current > vigenciaPagosContext.hasta
                  );
                }}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Hasta" name="hasta" rules={[{ required: true }]}>
              <CustomDatePicker
                style={{ width: '100%' }}
                placeholder="DD/MM/YYYY"
                format={dateFormat}
                onChange={onChangeVigenciaHasta}
                /* disabledDate={(current) => {
                  return (
                    vigenciaPagosContext &&
                    hastaMinPermitada &&
                    current < hastaMinPermitada
                  );
                }} */
                disabledDate={(current) => {
                  return (
                    vigenciaPagosContext && current < vigenciaPagosContext.desde
                  );
                }}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label="Días"
              name="cantidad_dias"
              rules={[{ required: true }]}
              // min={cantidadMinPermitada}
              onChange={(e) => debounceOnChangeVigenciaDias(e.target.value)}
              onKeyPress={formatNumberOnKeyPress}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1}
                onChange={(e) => debounceOnChangeVigenciaDias(e)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  useEffect(() => {
    const vigenciaFormNuevo = { ...vigenciaForm };
    if (datosSugeridosCargados || form) {
      if (form) {
        if (mostrarForm) {
          if (vigenciaFormNuevo && vigenciaFormNuevo.desde) {
            if (vigenciaFormNuevo.desde) {
              vigenciaFormNuevo.desde = moment(vigenciaFormNuevo.desde);
            }
            if (vigenciaFormNuevo.hasta) {
              vigenciaFormNuevo.hasta = moment(vigenciaFormNuevo.hasta);
            }
            form.setFieldsValue(vigenciaFormNuevo);
          }
        }
      }
      /* if (vigenciaByAseguradoraList[0].config) {
        validacionesVigenciaByAseguradora();
      } */
      validacionesVigenciaByAseguradora();
      if (onInit) {
        onInit({
          ...vigenciaPagosContext,
          ...vigenciaFormNuevo,
          mostrarForm
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vigenciaForm, mostrarForm, datosSugeridosCargados]); // form , mostrarForm, vigenciaForm

  useEffect(() => {
    if (
      vigenciaPagosContext &&
      vigenciaPagosContext?.desde &&
      !vigenciaForm.desde
    ) {
      // El plan ya se encuentra seleccionado con la Vigencia y Frecuencia de Pagos para la Aseguradora.
      const vigenciaAseguradoraSeleccionada = {
        ...vigenciaPagosContext.pagos,
        vigencia: vigenciaPagosContext.vigencia,
        desde: moment(vigenciaPagosContext.desde),
        hasta: moment(vigenciaPagosContext.hasta),
        cantidad_dias: Number(vigenciaPagosContext.cantidad_dias)
        // vigencia: vigenciaPagosContext.vigencia
      };
      setVigenciaForm(vigenciaAseguradoraSeleccionada);

      if (
        vigenciaPagosContext.vigenciasHabilitadas &&
        vigenciaPagosContext.vigenciasHabilitadas.length > 0
      ) {
        setVigenciasHabilitadas(
          ordenarVigencias(vigenciaPagosContext.vigenciasHabilitadas)
        );
      }

      if (!datosSugeridos) {
        // setMostrarForm(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vigenciaPagosContext]);

  useEffect(() => {
    if (
      vigenciasHabilitadas &&
      vigenciasHabilitadas.length > 0 &&
      datosSugeridos
    ) {
      if (!vigenciaPagosContext) {
        setVigenciaPagosContext({
          ...datosSugeridos,
          vigenciasHabilitadas
        });
      } else {
        setVigenciaPagosContext({
          ...vigenciaPagosContext,
          ...vigenciaForm,
          vigenciasHabilitadas
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vigenciasHabilitadas, datosSugeridos]);

  useEffect(() => {
    if (
      !datosSugeridos &&
      frecuenciasHabilitadas.length > 0 &&
      ((vigenciaPagosContext && !vigenciaPagosContext?.desde) ||
        !vigenciaPagosContext)
    ) {
      const load = async () => {
        //  await getDatosSugeridosFromConfig();
        const vigenciaSug = {
          cantidad_pagos: vigenciaSugeridaConfig.vigenciaSugerida.cantidadPagos,
          desde: moment(vigenciaSugeridaConfig.vigenciaSugerida.desde),
          hasta: moment(vigenciaSugeridaConfig.vigenciaSugerida.hasta),
          cantidad_dias: vigenciaSugeridaConfig.vigenciaSugerida.cantidadDias,
          vigencia: vigenciaSugeridaConfig.vigenciaSugerida.vigencia,
          id_vigencia:
            vigenciaSugeridaConfig.vigenciaSugerida.vigencia &&
            vigenciaSugeridaConfig.vigenciaSugerida.vigencia.id,
          frecuencia: vigenciaSugeridaConfig.vigenciaSugerida.frecuencia,
          id_modalidad_pago:
            vigenciaSugeridaConfig.vigenciaSugerida.frecuencia.id,
          pagos: {
            frecuencia: vigenciaSugeridaConfig.vigenciaSugerida.frecuencia,
            id_modalidad_pago:
              vigenciaSugeridaConfig.vigenciaSugerida.frecuencia.id,
            cantidad_pagos:
              vigenciaSugeridaConfig.vigenciaSugerida.cantidadPagos
          }
        };
        setDatosSugeridos(vigenciaSug);
        setVigenciaForm(vigenciaSug);
        setDatosSugeridosCargados(true);
      };
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frecuenciasHabilitadas]);

  useEffect(() => {
    if (vigenciaSugeridaConfig) {
      setVigenciasHabilitadas(
        vigenciaSugeridaConfig.vigenciaSugerida.vigencias
      );
      setFrecuenciasHabilitadas(
        ordenarFrecuencias(vigenciaSugeridaConfig.vigenciaSugerida.frecuencias)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vigenciaSugeridaConfig]);

  useEffect(() => {
    if (withForm) {
      setForm(withForm);
    }
  }, [withForm]);

  const descripcion = () => {
    const styles = {
      icon: {
        color: token.colorTextDisabled,
        fontSize: 16,
        paddingLeft: 8,
        paddingRight: 8
      }
    };
    if (screens && screens.md === true) {
      return (
        <span>
          Desde el{' '}
          <span
            style={{ fontWeight: 'bold' }}
          >{`${vigenciaPagosContext.desde.format('DD/MM/YYYY')}`}</span>
          <SwapRightOutlined size="large" style={styles.icon} />
          {`Hasta el `}
          <span
            style={{ fontWeight: 'bold' }}
          >{`${vigenciaPagosContext.hasta.format('DD/MM/YYYY')}`}</span>
          <Divider type="vertical" style={{ height: '1.4em' }} />
          {`con `}
          <span style={{ fontWeight: 'bold' }}>{`${cantidadPagosText()}`}</span>
        </span>
      );
    }
    if (screens && screens.md === false) {
      return (
        <div style={{ textAlign: 'center' }}>
          <span>{`${vigenciaPagosContext.desde.format('DD/MM/YYYY')}`}</span>{' '}
          <SwapRightOutlined size="large" style={styles.icon} />
          <span>{`${vigenciaPagosContext.hasta.format('DD/MM/YYYY')}`}</span>
          <br />
          {`con `}
          <span>{`${cantidadPagosText()}`}</span>
        </div>
      );
    }
    return '';
  };

  return (
    <>
      {!hidden && (
        <div
          style={{
            ...{
              width: '100%',
              marginTop: '0px',
              marginBottom: 16,
              textAlign: 'left',
              border: 'none'
            },
            ...style
          }}
          // bodyStyle={{ padding: 0 }}
        >
          {!mostrarForm ? (
            <div
              style={
                center
                  ? {
                      margin: 'auto',
                      width: 'fit-content'
                    }
                  : { width: 'fit-content' }
              }
            >
              <TextWithEditButton
                type={type}
                descripcion={vigenciaPagosContext && descripcion()}
                loading={loading}
                tituloEditTooltip="Editar Vigencia"
                onClick={() => setMostrarForm(true)}
              />
            </div>
          ) : (
            <>
              <Card
                size="small"
                title="Editar parámetros"
                style={{
                  border: `${CONFIG.COLOR_PRIMARY} 1px solid`,
                  textAlign: 'left',
                  maxWidth: 1000,
                  ...(center ? { margin: 'auto' } : {})
                }}
                extra={
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setMostrarForm(false)}
                  >
                    Minimizar
                  </Button>
                }
              >
                {!withForm ? (
                  <NewForm
                    setForm={setForm}
                    vigenciaForm={vigenciaForm}
                    setMostrarForm={setMostrarForm}
                  >
                    <Items />
                  </NewForm>
                ) : (
                  <Items />
                )}
              </Card>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default VigenciaForm;
